<script lang="tsx" setup>
import useModuleColumns from './columns'
import FilterContainer from '../filter/FilterContainer.vue'
import TextFilter from '../filter/TextFilter.vue'
import ResponsibleFilter from '../filter/ResponsibleFilter.vue'
import { selectItem, TextField, ValueField } from '@/models/select-item'
import { getModuleStatusText, getModuleTypeText } from '@/services/utils'
import { useI18n } from 'vue-i18n'
import MultiSelectFilter from '../filter/MultiSelectFilter.vue'
import { Applicable, Excluded, Inapplicable, Process, System } from '@/models/module-enums-generated'
import { onBeforeMount, ref } from 'vue'
import http from '@/services/http'
import type { Response } from '@/models/api-generated'
import SingleSelectFilter from '../filter/SingleSelectFilter.vue'
import InstanceFilter from '../filter/InstanceFilter.vue'

const props = defineProps<{
	component: string
}>()

const { t } = useI18n()
const { columns, custom, all } = useModuleColumns(props.component)
const isKeyOfColumns = (key: string): key is keyof typeof columns => key in columns
const versionOptions = ref<{ text: string; value: string }[]>([])

const setFilterFromParams = () => {
	const params = new URLSearchParams(window.location.search)
	for (const [key, value] of params) {
		// Key doesn't exist in columns or not a filter param.
		if (!isKeyOfColumns(key)) {
			continue
		}
		const column = columns[key].value
		if (key === 'responsible') {
			const parsed = JSON.parse(value)
			column.filter = [{ id: parsed.id, type: parsed.type }]
		} else if (key === 'code') {
			column.filter = value
		} else {
			const parsedNumber = parseInt(value)
			if (!isNaN(parsedNumber)) {
				column.filter.push(parsedNumber)
			} else {
				column.filter.push(value)
			}
		}
	}
}

const getVersions = async () => {
	const { data } = await http.get<Response<string[]>>('/api/versions')
	if (data.data) {
		versionOptions.value = data.data.map((v) => selectItem(v, v))
	}
}

onBeforeMount(() => {
	setFilterFromParams()
	getVersions()
})
</script>

<template>
	<FilterContainer :columns="all" :custom="custom" bookmark="modules-bookmark-">
		<div class="p-2 filter-col" v-if="columns.version.value.isVisible">
			<SingleSelectFilter
				id="version-filter-methods"
				v-model="columns.version.value"
				:label-field="TextField"
				:value-field="ValueField"
				:options="versionOptions"
			></SingleSelectFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.description.value.isVisible">
			<TextFilter id="description-filter-module" v-model="columns.description.value"></TextFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.code.value.isVisible">
			<TextFilter id="code-filter-module" v-model="columns.code.value"></TextFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.responsible.value.isVisible">
			<ResponsibleFilter id="responsible-filter-module" v-model="columns.responsible.value"></ResponsibleFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.type.value.isVisible">
			<MultiSelectFilter
				id="module-type-filter"
				v-model="columns.type.value"
				:label-field="TextField"
				:value-field="ValueField"
				:formatter="getModuleTypeText"
				:options="[
					{ text: t('module.processModule'), value: Process },
					{ text: t('module.systemModule'), value: System },
				]"
			></MultiSelectFilter>
		</div>

		<div class="p-2 filter-col" v-if="columns.active.value.isVisible">
			<MultiSelectFilter
				id="module-status-filter"
				v-model="columns.active.value"
				:label-field="TextField"
				:value-field="ValueField"
				:formatter="getModuleStatusText"
				:options="[
					{ text: t('module.status.applicable'), value: Applicable },
					{ text: t('module.status.inapplicable'), value: Inapplicable },
					{ text: t('module.status.excluded'), value: Excluded },
				]"
			></MultiSelectFilter>
		</div>

		<div class="p-2 filter-col" v-show="columns.instance.value.isVisible">
			<InstanceFilter v-model="columns.instance.value"></InstanceFilter>
		</div>
	</FilterContainer>
</template>
